/**
 * Focus management for dialogs (drawer, sheet, modal, mobile nav).
 *
 * While `active`: moves focus into the container, keeps Tab / Shift+Tab inside
 * it, calls `onEscape` on Escape, and restores focus to the previously focused
 * element when deactivated. Also locks body scroll so the page under a sheet
 * does not move (paired with `overscroll-behavior: contain` on the panel).
 */
import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('data-focus-guard') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export interface FocusTrapOptions {
  active: boolean;
  onEscape?: () => void;
  /** Selector for the element to focus first; falls back to the first focusable. */
  initialFocus?: string;
  lockScroll?: boolean;
}

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  { active, onEscape, initialFocus, lockScroll = true }: FocusTrapOptions,
): void {
  // Callers usually pass an inline arrow for `onEscape`. Reading it through a
  // ref keeps it out of the effect deps, so a parent re-render does not tear
  // the trap down and re-arm it (which re-focused the panel on every render
  // and made the final "restore focus" point at the panel's own button).
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = 'hidden';

    const focusFirst = () => {
      const target =
        (initialFocus ? root.querySelector<HTMLElement>(initialFocus) : null) ??
        focusableIn(root)[0] ??
        root;
      target.focus({ preventScroll: true });
    };
    // Defer one frame so the panel's enter transition does not fight the scroll.
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscapeRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(root);
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (current === first || !root.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !root.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      if (lockScroll) document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [active, ref, initialFocus, lockScroll]);
}
